import React from "react";
import {
  Modal as MuiModal,
  ModalProps as MuiModalProps,
  Snackbar,
  Alert,
} from "@mui/material";
import { Box } from "@/components/atoms";
import { Company } from "@/types/company";
import { ModalContent } from "./ModalContent";
import { ButtonSetProps } from "../ButtonSet";

export interface ModalProps extends Omit<MuiModalProps, "children"> {
  componentName: string;
  header: string;
  subheader?: string;
  children?: React.ReactNode;
  logoUrl?: Company["logo_url"];
  loading?: boolean;
  error?: string | null;
  snackbar?: {
    state: boolean;
    msg: string;
    severity: "success" | "error";
    onClose: () => void;
  };
  bookmarked?: boolean;
  onBookmarkToggle?: () => void;
  showBookmark?: boolean;
  disableBookmark?: boolean;
  onCloseButtonClick?: () => void;
  renderButtonSet?: boolean;
  handleRightButtonClick?: ButtonSetProps["rightBoxHandler"];
  rightButtonText?: ButtonSetProps["rightBoxText"];
  disableRightButton?: boolean;
  handleLeftButtonClick?: ButtonSetProps["leftBoxHandler"];
  leftButtonText?: ButtonSetProps["leftBoxText"];
  disableButtonSet?: boolean;
  handleFlagErrorClick?: () => void;
  isJobCard?: boolean;
}

export const Modal = ({
  componentName,
  header,
  subheader,
  children,
  logoUrl,
  loading,
  error,
  snackbar,
  bookmarked,
  onBookmarkToggle,
  showBookmark = false,
  disableBookmark = false,
  onCloseButtonClick,
  renderButtonSet,
  handleRightButtonClick,
  handleLeftButtonClick,
  rightButtonText = "Save",
  disableRightButton,
  disableButtonSet,
  handleFlagErrorClick,
  isJobCard = false,
  ...props
}: ModalProps) => {
  return (
    <MuiModal
      data-testid={`${componentName}-modal`}
      onClose={onCloseButtonClick}
      {...props}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      slotProps={{
        backdrop: {
          style: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          },
        },
      }}
    >
      <Box
        componentName={`${componentName}-parent`}
        alignItems="center"
        justifyContent="center"
        sx={{
          display: "flex",
          flexDirection: "column",
          rowGap: "1rem",
          width: "fit-content",
          maxWidth: "600px",
          minWidth: "320px",
          maxHeight: "100%",
          backgroundColor: "white",
          border: "2px solid",
          borderRadius: "1em",
          p: 2,
        }}
      >
        {snackbar && (
          <Snackbar
            open={snackbar.state}
            autoHideDuration={4000}
            onClose={snackbar.onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              severity={snackbar.severity}
              variant="filled"
              onClose={snackbar.onClose}
            >
              {snackbar.msg}
            </Alert>
          </Snackbar>
        )}
        <Box componentName={`${componentName}-modal-content`} border={false}>
          <ModalContent
            componentName={componentName}
            children={children}
            header={header}
            subheader={subheader}
            error={error}
            loading={loading}
            logoUrl={logoUrl}
            bookmarked={bookmarked}
            onBookmarkToggle={onBookmarkToggle}
            showBookmark={showBookmark}
            disableBookmark={disableBookmark}
            onCloseButtonClick={onCloseButtonClick}
            renderButtonSet={renderButtonSet}
            handleRightButtonClick={handleRightButtonClick}
            rightButtonText={rightButtonText}
            disableButtonSet={disableButtonSet}
            handleFlagErrorClick={handleFlagErrorClick}
            isJobCard={isJobCard}
            disableRightButton={disableRightButton}
            handleLeftButtonClick={handleLeftButtonClick}
          />
        </Box>
      </Box>
    </MuiModal>
  );
};
